'use memo';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { AlertCard } from '@/components/AlertCard';
import { HStack, VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { Text } from '@/components/Typography/Text';
import { toast } from '@/components/ui/use-toast';
import { accessRulesKeyPrefix, authPoliciesKeyPrefix, toAccessRuleFormValue, useAccessRule, useAuthenticationPolicyList, useSoftwareNameLookup, useUpdateAccessRule } from '@/features/authentication-policy';
import { handleApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { formatDateTimeToLocal } from '@/utils/date/formatDateTimeToLocal';
import { AccessRuleStepperScreen } from '../-components/AccessRuleStepperScreen';
export const Route = createFileRoute('/_staff/settings/authentication/_layout/access-rules/$id/')({
  component: EditAccessRulePage
});
function EditAccessRulePage() {
  const {
    id
  } = Route.useParams();
  const {
    i18n,
    t
  } = useTranslation(defaultNS, {
    keyPrefix: accessRulesKeyPrefix
  });
  const {
    t: tPolicies
  } = useTranslation(defaultNS, {
    keyPrefix: authPoliciesKeyPrefix
  });
  const navigate = useNavigate();
  const {
    data,
    isPending,
    isError
  } = useAccessRule(id);
  const policyQuery = useAuthenticationPolicyList();
  const softwareIds = data?.applicationScope ?? [];
  const {
    resolveSoftwareName,
    isPending: isSoftwareLookupPending,
    isError: isSoftwareLookupError
  } = useSoftwareNameLookup(softwareIds);
  const updateMutation = useUpdateAccessRule();
  const [errorMessage, setErrorMessage] = useState('');
  const baseLink = `/settings/authentication/access-rules/${id}`;
  const backToList = () => navigate({
    to: '/settings/authentication/access-rules'
  });
  if (isPending || isSoftwareLookupPending || policyQuery.isPending) {
    return <ViewportLoader />;
  }
  if (isError || data == null) {
    return <VStack className="w-full gap-4 p-10">
        <AlertCard variant="mutedError" description={t($ => $.list.loadError)} />
      </VStack>;
  }
  if (policyQuery.isError || policyQuery.data == null) {
    return <VStack className="w-full gap-4 p-10">
        <AlertCard variant="mutedError" description={tPolicies($ => $.list.loadError)} />
      </VStack>;
  }
  return <VStack className="h-full min-h-0 gap-0">
      {isSoftwareLookupError && <VStack className="px-10 pt-6">
          <AlertCard variant="mutedError" description={t($ => $.form.softwareNameLoadError)} />
        </VStack>}
      <AccessRuleStepperScreen key={data.id} baseLink={baseLink} isEdit initialValue={toAccessRuleFormValue(data, resolveSoftwareName)} isSubmitting={updateMutation.isPending} errorMessage={errorMessage} meta={<HStack className="flex-wrap gap-x-4 gap-y-1">
            <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
              {t($ => $.form.meta.updated, {
          date: formatDateTimeToLocal(new Date(data.updatedAt), i18n.language, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          })
        })}
            </Text>
            <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
              {t($ => $.form.meta.policy, {
          name: data.policy.name
        })}
            </Text>
          </HStack>} onCancel={() => void backToList()} onSubmit={draft => {
      setErrorMessage('');
      updateMutation.mutate({
        ...draft,
        id
      }, {
        onSuccess: () => {
          toast({
            description: t($ => $.toast.updated)
          });
          void backToList();
        },
        onError: error => {
          setErrorMessage(handleApiError(error, {
            language: i18n.language
          }).message);
        }
      });
    }} />
    </VStack>;
}